import { z } from "zod";
import { DerivedDocumentStateSchema } from "./documentProcessingStatus";

/**
 * Per-document, per-step processing pipeline status streamed from the backend
 * SSE endpoint
 * `/controls/v2/control-session/{id}/document-processing-status/stream`.
 *
 * Field names are camelCase to match the backend's `to_camel` alias serializer.
 */

export const PipelineStepStatusSchema = z.enum([
  "pending",
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
]);

export const DocumentStepStatusSchema = z.object({
  step: z.string(),
  status: PipelineStepStatusSchema,
  // ISO timestamps; kept as lenient strings to avoid brittle format validation.
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  retryCount: z.number().int(),
});

export const DocumentPipelineStatusSchema = z.object({
  documentId: z.string(),
  fileName: z.string().nullable(),
  workflowRunId: z.string().nullable(),
  derivedState: DerivedDocumentStateSchema,
  currentStep: z.string().nullable(),
  steps: z.array(DocumentStepStatusSchema),
});

export const ControlSessionPipelineStatusSchema = z.object({
  controlSessionId: z.string(),
  derivedState: DerivedDocumentStateSchema,
  documents: z.array(DocumentPipelineStatusSchema),
  source: z.enum(["hatchet", "fallback"]),
});

export const ControlPipelineStatusSchema = z.object({
  controlId: z.string(),
  derivedState: DerivedDocumentStateSchema,
  documents: z.array(DocumentPipelineStatusSchema),
  source: z.enum(["hatchet", "fallback"]),
});

/**
 * Work-step-session sibling of {@link ControlSessionPipelineStatusSchema},
 * streamed from
 * `/work-steps/work-step-session/{id}/document-processing-status/stream`.
 * Same shape, keyed by `workStepSessionId` instead of `controlSessionId`.
 */
export const WorkStepSessionPipelineStatusSchema = z.object({
  workStepSessionId: z.string(),
  derivedState: DerivedDocumentStateSchema,
  documents: z.array(DocumentPipelineStatusSchema),
  source: z.enum(["hatchet", "fallback"]),
});

export type PipelineStepStatus = z.infer<typeof PipelineStepStatusSchema>;
export type DocumentStepStatus = z.infer<typeof DocumentStepStatusSchema>;
export type DocumentPipelineStatus = z.infer<
  typeof DocumentPipelineStatusSchema
>;
export type ControlSessionPipelineStatus = z.infer<
  typeof ControlSessionPipelineStatusSchema
>;
export type ControlPipelineStatus = z.infer<typeof ControlPipelineStatusSchema>;
export type WorkStepSessionPipelineStatus = z.infer<
  typeof WorkStepSessionPipelineStatusSchema
>;
